import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Card, CardContent, Skeleton, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { activityApi } from '@/api/activityApi'
import { ActivityDayDialog } from '@/components/activity/ActivityDayDialog'
import { ActivityStreakCard } from '@/components/activity/ActivityStreakCard'
import { ActivityTimeline } from '@/components/activity/ActivityTimeline'
import { ActivityTopDays } from '@/components/activity/ActivityTopDays'
import { ActivityTypeFilter } from '@/components/activity/ActivityTypeFilter'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { HeatmapGrid } from '@/components/dashboard/widgets/HeatmapGrid'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { useAuthStore } from '@/stores/authStore'
import type { ActivityType } from '@/types/activity'
import type { HeatmapPoint } from '@/types/dashboard'

interface ActivityPageProps {
  /** Чей журнал показываем; без него — свой */
  userId?: number
  /** Имя пользователя в заголовке (просмотр ученика учителем/админом) */
  userName?: string
  /** Роли субъекта журнала (slug'и) — прячут неактуальные lesson-фильтры */
  subjectRoles?: string[]
}

/**
 * Экран активности: тепловая карта во всю ширину, слева фильтры и топ дней,
 * справа вертикальный таймлайн с подгрузкой. Используется тремя маршрутами:
 * свой профиль, ученик (учитель) и пользователь (админ).
 */
export function ActivityPage({ userId, userName, subjectRoles }: ActivityPageProps) {
  const { t } = useTranslation()
  const authUser = useAuthStore((state) => state.user)

  /**
   * Субъект журнала задаём явно даже для своей страницы: без user_id бэкенд
   * отдаёт учителю сводный heatmap по ученикам, и график расходился бы с лентой.
   */
  const effectiveUserId = userId ?? authUser?.id

  // Свой журнал — роли из авторизации; чужой — роли, переданные обёрткой
  const roles = useMemo(() => {
    if (userId !== undefined) return subjectRoles
    return authUser?.roles?.map((role) => role.slug)
  }, [userId, subjectRoles, authUser])

  const [types, setTypes] = useState<ActivityType[]>([])
  const [days, setDays] = useState<HeatmapPoint[]>([])
  const [range, setRange] = useState<{ from: string; to: string } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  const typesKey = useMemo(() => types.join(','), [types])

  useEffect(() => {
    if (effectiveUserId === undefined) {
      // Субъект журнала неизвестен (профиль ещё не загружен или сессия
      // оборвалась). Загрузку обязательно снимаем — иначе скелетон остался бы
      // висеть навсегда; запрос без user_id не делаем, он вернул бы учителю
      // сводные данные по ученикам вместо журнала конкретного человека.
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    activityApi
      .heatmap({ userId: effectiveUserId, types })
      .then((response) => {
        if (cancelled) return
        setDays(response.days)
        setRange({ from: response.from, to: response.to })
        setError(null)
      })
      .catch((caught: unknown) => {
        if (cancelled) return
        setError(displayErrorMessage(getErrorMessage(caught), t('common.error')))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveUserId, typesKey])

  const handleDayClick = useCallback((date: string) => setSelectedDate(date), [])

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pt: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          {userName ? t('activity.titleFor', { name: userName }) : t('activity.title')}
        </Typography>
        <PageHelpPopover pageKey="activity" />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {effectiveUserId === undefined ? (
        <Alert severity="warning">{t('activity.subjectUnavailable')}</Alert>
      ) : (
        <>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              {loading || !range ? (
                <Skeleton variant="rectangular" height={140} />
              ) : (
                <HeatmapGrid
                  from={range.from}
                  to={range.to}
                  days={days}
                  showLabels
                  fill
                  showLegend
                  onDayClick={handleDayClick}
                />
              )}
            </CardContent>
          </Card>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 320px) 1fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Stack spacing={2}>
              <Card>
                <CardContent>
                  {loading ? (
                    <Skeleton variant="rectangular" height={72} />
                  ) : (
                    <ActivityStreakCard days={days} />
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <ActivityTypeFilter value={types} onChange={setTypes} roles={roles} />
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  {loading ? (
                    <Skeleton variant="rectangular" height={180} />
                  ) : (
                    <ActivityTopDays days={days} onDayClick={handleDayClick} />
                  )}
                </CardContent>
              </Card>
            </Stack>

            <Card>
              <CardContent>
                <ActivityTimeline userId={effectiveUserId} types={types} perPage={20} />
              </CardContent>
            </Card>
          </Box>

          <ActivityDayDialog
            open={selectedDate !== null}
            date={selectedDate}
            onClose={() => setSelectedDate(null)}
            userId={effectiveUserId}
            types={types}
          />
        </>
      )}
    </Box>
  )
}
